import type { CapabilityReport, RuntimeMode } from "@/runtime/types";

export interface CapabilityEnvironment {
  secureContext: boolean;
  xr: Pick<XRSystem, "isSessionSupported"> | undefined;
  mediaDevices: Pick<MediaDevices, "getUserMedia"> | undefined;
  coarsePointer: boolean;
  iosVersion?: string | null;
  appleMobile?: boolean;
  webgl?: boolean;
  orientation?: boolean;
  wasmSimd?: boolean;
  eighthWall?: boolean;
}

export async function detectCapabilities(environment: CapabilityEnvironment = browserEnvironment()): Promise<CapabilityReport> {
  const webxr = environment.secureContext && Boolean(
    environment.xr && await environment.xr.isSessionSupported("immersive-ar").catch(() => false),
  );
  const camera = environment.secureContext && Boolean(environment.mediaDevices?.getUserMedia);
  const iosVersion = environment.iosVersion ?? null;
  const appleMobile = environment.appleMobile ?? iosVersion !== null;
  const webgl = environment.webgl ?? false;
  const orientation = environment.orientation ?? false;
  const wasmSimd = environment.wasmSimd ?? false;
  const eighthWallEligible = environment.secureContext
    && camera
    && appleMobile
    && isVersionAtLeast(iosVersion, 16, 4)
    && webgl
    && orientation
    && wasmSimd
    && (environment.eighthWall ?? true);
  return {
    secureContext: environment.secureContext,
    webxr,
    camera,
    coarsePointer: environment.coarsePointer,
    appleMobile,
    iosVersion,
    webgl,
    orientation,
    wasmSimd,
    eighthWallEligible,
    preferredMode: chooseRuntimeMode({ webxr, camera, coarsePointer: environment.coarsePointer, eighthWallEligible }),
  };
}

export function chooseRuntimeMode(capability: Pick<CapabilityReport, "webxr" | "camera" | "coarsePointer"> & { eighthWallEligible?: boolean }): RuntimeMode {
  if (capability.webxr) return "webxr";
  if (capability.eighthWallEligible) return "eighth-wall";
  if (capability.camera && capability.coarsePointer) return "camera-viewer";
  return "non-ar";
}

function browserEnvironment(): CapabilityEnvironment {
  return {
    secureContext: window.isSecureContext,
    xr: navigator.xr,
    mediaDevices: navigator.mediaDevices,
    coarsePointer: window.matchMedia("(pointer: coarse)").matches,
    iosVersion: detectIosVersion(navigator.userAgent),
    appleMobile: isAppleMobile(navigator),
    webgl: supportsWebGl(),
    orientation: "DeviceOrientationEvent" in window,
    wasmSimd: supportsWasmSimd(),
    eighthWall: true,
  };
}

export function detectIosVersion(userAgent: string): string | null {
  const match = userAgent.match(/(?:CPU (?:iPhone )?OS|iPhone OS) (\d+)[_.](\d+)/i);
  if (match) return `${match[1]}.${match[2]}`;
  const safariVersion = userAgent.match(/Version\/(\d+)\.(\d+).+Mobile\//i);
  return safariVersion ? `${safariVersion[1]}.${safariVersion[2]}` : null;
}

export function isVersionAtLeast(version: string | null, major: number, minor: number): boolean {
  if (!version) return false;
  const [actualMajor = 0, actualMinor = 0] = version.split(".").map(Number);
  return actualMajor > major || (actualMajor === major && actualMinor >= minor);
}

function isAppleMobile(navigatorLike: Navigator): boolean {
  return /iPad|iPhone|iPod/.test(navigatorLike.userAgent)
    || (navigatorLike.platform === "MacIntel" && navigatorLike.maxTouchPoints > 1);
}

export function supportsWebGl(documentLike: Pick<Document, "createElement"> = document): boolean {
  try {
    const canvas = documentLike.createElement("canvas");
    const context = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!context) return false;
    context.getExtension?.("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

function supportsWasmSimd(): boolean {
  if (typeof WebAssembly?.validate !== "function") return false;
  return WebAssembly.validate(new Uint8Array([
    0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123,
    3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
  ]));
}
